import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Sparkles, Save } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { marketingService } from "@/services/marketingService";
import { resaService } from "@/services/resaService";
import { PLATFORMS, CONTENT_TYPES } from "@/config/marketingOptions";
import { Field } from "@/components/profile/FormControls";
import { RelatedEntityPicker } from "@/components/run/RelatedEntityPicker";
import { parseFieldErrors } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { title: "", platform: "instagram", content_type: "social_post", topic: "", goal: "", audience: "", hook: "", body: "", cta: "", hashtags: "", notes: "", scheduled_for: "" };

export default function ContentStudio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(empty);
  const [related, setRelated] = useState(null);
  const [brand, setBrand] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await resaService.generateMarketing({
        platform: f.platform, content_type: f.content_type, topic: f.topic || null,
        goal: f.goal || null, audience: f.audience || null, hook: f.hook || null,
        related_entity_type: related?.type || null, related_entity_id: related?.id || null,
      });
      const g = res.generated || {};
      setF((p) => ({ ...p, hook: g.hook || p.hook, body: g.body || p.body, cta: g.cta || p.cta, hashtags: g.hashtags || p.hashtags }));
      toast.success("RESA drafted your content — review and edit before saving");
    } catch (err) {
      toast.error(err?.message || "RESA couldn't generate content right now");
    } finally { setGenerating(false); }
  };

  useEffect(() => {
    marketingService.brandContext().then(setBrand).catch(() => {});
    if (id) {
      marketingService.get(id).then((c) => {
        setF({ title: c.title || "", platform: c.platform, content_type: c.content_type, topic: c.topic || "", goal: c.goal || "", audience: c.audience || "", hook: c.hook || "", body: c.body || "", cta: c.cta || "", hashtags: c.hashtags || "", notes: c.notes || "", scheduled_for: (c.scheduled_for || "").slice(0, 10) });
        setRelated(c.related?.id ? c.related : null);
      }).catch(() => toast.error("Could not load content")).finally(() => setLoading(false));
    }
  }, [id]);

  const applyBrandCta = () => { if (brand?.default_cta) set("cta", brand.default_cta); };
  const applyAudience = () => { if (brand?.target_audience) set("audience", brand.target_audience); };

  const save = async (thenPlan = false) => {
    setErrors({}); setSaving(true);
    try {
      const payload = { ...f, scheduled_for: f.scheduled_for || null, related_entity_type: related?.type || null, related_entity_id: related?.id || null };
      const saved = id ? await marketingService.update(id, payload) : await marketingService.create(payload);
      if (thenPlan && saved.status === "draft") await marketingService.setStatus(saved.id, "planned");
      toast.success(id ? "Content saved" : "Draft created");
      navigate("/marketing");
    } catch (err) { setErrors(parseFieldErrors(err)); toast.error(err.message || "Check the highlighted fields"); }
    finally { setSaving(false); }
  };

  if (loading) return <AppShell><div className="py-20 text-center text-muted-foreground">Loading…</div></AppShell>;

  return (
    <AppShell>
      <div className="space-y-6" data-testid="content-studio">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/marketing")} data-testid="studio-back"><ArrowLeft className="h-4 w-4" /> Marketing</Button>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-accent">AgentHQ · Marketing</p>
            <h1 className="font-heading text-3xl font-bold text-foreground">{id ? "Edit Content" : "Content Studio"}</h1>
          </div>
          <Button onClick={generate} disabled={generating} data-testid="resa-generate" className="gap-1.5 bg-brand-navy hover:bg-brand-deep-blue">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-brand-gold" />}
            {generating ? "Generating…" : "Generate with RESA"}
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-5">
          {/* Left: setup */}
          <Card className="lg:col-span-2"><CardContent className="space-y-4 p-5">
            <h2 className="text-sm font-bold text-foreground">Setup</h2>
            <Field label="Internal title" error={errors.title}><Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Just Listed — 42 Oak St" data-testid="studio-title" /></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Platform"><Select value={f.platform} onValueChange={(v) => set("platform", v)}><SelectTrigger data-testid="studio-platform"><SelectValue /></SelectTrigger><SelectContent>{PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Content type"><Select value={f.content_type} onValueChange={(v) => set("content_type", v)}><SelectTrigger data-testid="studio-type"><SelectValue /></SelectTrigger><SelectContent>{CONTENT_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select></Field>
            </div>
            <Field label="Topic"><Input value={f.topic} onChange={(e) => set("topic", e.target.value)} data-testid="studio-topic" /></Field>
            <Field label="Goal"><Input value={f.goal} onChange={(e) => set("goal", e.target.value)} data-testid="studio-goal" /></Field>
            <Field label="Audience">
              <Input value={f.audience} onChange={(e) => set("audience", e.target.value)} data-testid="studio-audience" />
              {brand?.target_audience && <button onClick={applyAudience} className="mt-1 text-xs text-accent hover:underline" data-testid="use-brand-audience">Use brand audience</button>}
            </Field>
            <Field label="Link to (optional)"><RelatedEntityPicker value={related} onChange={setRelated} allowedTypes={["contact", "transaction"]} /></Field>
            <Field label="Plan for (optional)"><Input type="date" value={f.scheduled_for} onChange={(e) => set("scheduled_for", e.target.value)} data-testid="studio-date" /></Field>
            <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="studio-notes" /></Field>
          </CardContent></Card>

          {/* Right: draft editor */}
          <Card className="lg:col-span-3"><CardContent className="space-y-4 p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-foreground">Draft</h2>
              {brand?.brand_voice?.length ? <span className="text-xs text-muted-foreground">Brand voice: {brand.brand_voice.join(", ")}</span> : null}
            </div>
            <Field label="Hook"><Input value={f.hook} onChange={(e) => set("hook", e.target.value)} placeholder="Opening line that grabs attention" data-testid="studio-hook" /></Field>
            <Field label="Caption / Body"><Textarea rows={10} value={f.body} onChange={(e) => set("body", e.target.value)} placeholder="Write your caption or script…" data-testid="studio-body" /></Field>
            <Field label="Call to action">
              <Input value={f.cta} onChange={(e) => set("cta", e.target.value)} data-testid="studio-cta" />
              {brand?.default_cta && <button onClick={applyBrandCta} className="mt-1 text-xs text-accent hover:underline" data-testid="use-brand-cta">Use brand default CTA</button>}
            </Field>
            <Field label="Hashtags"><Textarea rows={2} value={f.hashtags} onChange={(e) => set("hashtags", e.target.value)} placeholder="#realestate #justlisted" data-testid="studio-hashtags" /></Field>
          </CardContent></Card>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="studio-save-draft">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Draft</Button>
          <Button onClick={() => save(true)} disabled={saving} data-testid="studio-save-plan">Save &amp; Plan</Button>
        </div>
      </div>
    </AppShell>
  );
}
